import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import WebsiteLayout from '../components/WebsiteLayout';
import { useLanguage } from '../../../hooks/useLanguage';

const HERO_IMAGE = '/assets/website/photos/asclepius-510k-hero.png';

const Asclepius510k: React.FC = () => {
  const [language] = useLanguage();
  const en = language === 'en';

  const customCta = (
    <div className="site-end__cta">
      <span className="site-end__label">
        {en ? 'Early access' : 'Vroege toegang'}
      </span>
      <h2 className="site-end__title">
        {en
          ? "We're bringing the model to a small number of teams first."
          : 'We brengen het model eerst naar een klein aantal teams.'}
      </h2>
      <p className="site-end__sub">
        {en
          ? "We're working with a handful of regulatory-affairs teams on real submissions, tuned to their device families. If that's you, tell us the problem you're on."
          : 'We werken met een handvol regulatory-affairs (RA) teams aan echte indieningen, afgestemd op hun productfamilies. Is dat jij? Vertel ons dan waar je aan zit.'}
      </p>
      <div className="site-end__action">
        <Link className="btn btn-primary" to="/contact?prefill=fda-510k">
          {en ? 'Request early access' : 'Vraag vroege toegang aan'}
        </Link>
      </div>
    </div>
  );

  const descriptionMeta = en
    ? 'An AI model built and trained for 510(k) predicate search and submission preparation. Finds predicates, builds substantial-equivalence tables. Purpose-made on a proven, regulated-domain architecture.'
    : 'Een AI-model gebouwd en getraind voor 510(k)-predicate-search en voorbereiding van de indiening. Vindt predicates, stelt tabellen voor substantiële gelijkwaardigheid op. Doelgericht gebouwd op een bewezen architectuur voor gereguleerde domeinen.';

  return (
    <WebsiteLayout bodyClass="fda510" footerVariant="fda" customCta={customCta}>
      <Helmet>
        <title>A model for 510(k) predicate search &amp; submission prep · Thoughtful Oasis</title>
        <meta name="description" content={descriptionMeta} />
        <link
          rel="canonical"
          href="https://thoughtful-oasis.com/models/asclepius-510k"
        />
        <meta property="og:type" content="product" />
        <meta property="og:site_name" content="Thoughtful Oasis" />
        <meta
          property="og:title"
          content="A model for 510(k) predicate search & submission prep · Thoughtful Oasis"
        />
        <meta
          property="og:description"
          content="An AI model built and trained for 510(k) predicate search and submission preparation. It finds predicates and builds substantial-equivalence tables — purpose-made, not a general model adapted to the task."
        />
        <meta
          property="og:url"
          content="https://thoughtful-oasis.com/models/asclepius-510k"
        />
        <meta
          property="og:image"
          content="https://thoughtful-oasis.com/assets/website/photos/og-home.jpg"
        />
        <meta property="og:locale" content={en ? 'en_GB' : 'nl_NL'} />
      </Helmet>

      <main className="fda510">
        {/* Series ribbon */}
        <div className="ribbon">
          <div className="ribbon__inner">
            <span className="ribbon__dot" aria-hidden="true"></span>
            <span>Powered by oasis_0</span>
          </div>
        </div>

        {/* Hero */}
        <header className="fhero">
          <div className="container">
            <span className="label">
              {en
                ? 'Introducing oasis_0 Asclepius 510k'
                : 'Dit is oasis_0 Asclepius 510k'}
            </span>
            <h1>
              {en ? (
                <>
                  A model built for{' '}
                  <span className="accent">510(k) predicate search</span> and submission
                  preparation.
                </>
              ) : (
                <>
                  Een model gebouwd voor{' '}
                  <span className="accent">510(k) predicate-search</span> en voorbereiding
                  van de indiening.
                </>
              )}
            </h1>
            <p className="fhero__sub">
              {en
                ? 'General-purpose models and existing 510(k) search tools each do part of this well. Neither was designed to produce a defensible, characteristic-by-characteristic equivalence argument. This model was — trained for the task, on an architecture we already run in regulated, high-accountability settings. It finds predicates and builds the substantial-equivalence table, and every result traces back to the characteristics behind it.'
                : 'Generieke modellen en bestaande 510(k)-zoektools doen elk een deel hiervan goed. Geen van beide is ontworpen om een verdedigbaar, kenmerk-voor-kenmerk gelijkwaardigheidsbetoog te maken. Dit model wel — getraind voor de taak, op een architectuur die we al draaien in gereguleerde omgevingen met hoge verantwoordingsdruk. Het vindt predicates en bouwt de tabel voor substantiële gelijkwaardigheid, en elk resultaat is terug te herleiden tot de kenmerken erachter.'}
            </p>
            <div className="fhero__cta">
              <Link className="btn btn-primary" to="/contact?prefill=fda-510k">
                {en ? 'Request early access' : 'Vraag vroege toegang aan'}
              </Link>
              <a className="btn btn-secondary" href="#what">
                {en ? 'See what it does' : 'Zie wat het doet'}
              </a>
            </div>
          </div>
          <div className="container fhero__media">
            <div className="fhero__frame">
              <img
                src={HERO_IMAGE}
                alt={
                  en
                    ? 'A subject device — a wearable single-lead ECG patch — matched to a ranked set of candidate 510(k) predicates, each scored per characteristic, with one predicate expanded into a substantial-equivalence comparison (same, different, gap) and the residual gap noted below.'
                    : 'Een subject device — een draagbare single-lead ECG-patch — gematcht aan een gerangschikte set kandidaat-510(k)-predicates, elk gescoord per kenmerk, met één predicate uitgeklapt in een vergelijking voor substantiële gelijkwaardigheid (gelijk, verschillend, gat) en het resterende gat eronder genoteerd.'
                }
              />
            </div>
            <p className="fhero__cap">
              {en ? 'Illustrative view.' : 'Illustratieve weergave.'}
            </p>
          </div>
        </header>

        {/* Why a dedicated model */}
        <section className="section">
          <div className="container">
            <div className="split">
              <div className="section-head">
                <span className="label">
                  {en ? 'Why a dedicated model' : 'Waarom een specifiek model'}
                </span>
                <h2>
                  {en
                    ? 'A different design goal: traceable, not just fluent.'
                    : 'Een ander ontwerpdoel: herleidbaar, niet alleen vloeiend.'}
                </h2>
                <p className="lede">
                  {en
                    ? 'In a submission, an answer you can defend is worth more than one that only reads well.'
                    : 'In een indiening is een antwoord dat je kunt verdedigen meer waard dan een dat alleen prettig leest.'}
                </p>
              </div>
              <div>
                <p className="body-text">
                  {en ? (
                    <>
                      Large language models are optimised for fluent, general-purpose text; existing
                      510(k) tools for fast lookup over the FDA database. Both are useful. But a
                      substantial-equivalence argument is neither a lookup nor a paragraph — it's a
                      characteristic-by-characteristic comparison you have to stand behind.{' '}
                      <strong>This model is built for that</strong>: it works from the same named
                      characteristics the FDA compares — intended use, energy source, sensing
                      modality, materials — so what it returns can be inspected, not just trusted.
                    </>
                  ) : (
                    <>
                      Grote taalmodellen zijn geoptimaliseerd voor vloeiende, algemene tekst;
                      bestaande 510(k)-tools voor snel opzoeken in de FDA-database. Beide zijn
                      nuttig. Maar een gelijkwaardigheidsbetoog is geen opzoekactie en geen alinea —
                      het is een kenmerk-voor-kenmerk vergelijking waar je achter moet staan.{' '}
                      <strong>Dit model is daarvoor gebouwd</strong>: het werkt vanuit dezelfde
                      benoemde kenmerken die de FDA vergelijkt — beoogd gebruik, energiebron,
                      meetmodaliteit, materialen — zodat je wat het teruggeeft kunt inspecteren,
                      niet alleen vertrouwen.
                    </>
                  )}
                </p>
                <p className="body-text">
                  {en
                    ? "It's the difference between an answer you verify from scratch and one that arrives with its reasons attached. In regulatory work, that's the part that matters."
                    : 'Het is het verschil tussen een antwoord dat je van nul moet verifiëren en een dat binnenkomt met de redenen erbij. In regelgevingswerk is dat het deel dat telt.'}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* What it does */}
        <section className="section band-dark" id="what">
          <div className="container">
            <div className="section-head">
              <span className="label">{en ? 'What it does' : 'Wat het doet'}</span>
              <h2>
                {en
                  ? 'It finds the predicate and drafts the equivalence table. You keep the judgment.'
                  : 'Het vindt de predicate en schrijft een eerste gelijkwaardigheidstabel. Jij houdt het oordeel.'}
              </h2>
              <p className="lede">
                {en
                  ? 'You describe the device as you would to the FDA. The model does the matching and the first draft.'
                  : 'Je beschrijft het hulpmiddel zoals je dat aan de FDA zou doen. Het model doet de matching en het eerste concept.'}
              </p>
            </div>
          </div>
          <div className="steps">
            <div className="step">
              <p className="step__num">{en ? '01 · You' : '01 · Jij'}</p>
              <h3>{en ? 'Describe the device.' : 'Beschrijf het hulpmiddel.'}</h3>
              <p className="step__body">
                {en ? (
                  <>
                    Intended use, patient population, energy source, materials, sensing modality —
                    including what it explicitly is <em>not</em>.
                  </>
                ) : (
                  <>
                    Beoogd gebruik, patiëntenpopulatie, energiebron, materialen, meetmodaliteit —
                    inclusief wat het uitdrukkelijk <em>niet</em> is.
                  </>
                )}
              </p>
              <div className="step__example">
                {en
                  ? '"Wearable single-lead ECG patch · arrhythmia detection · wireless · NOT prescription-only · NOT implantable."'
                  : '"Draagbare single-lead ECG-patch · aritmiedetectie · draadloos · NIET alleen op recept · NIET implanteerbaar."'}
              </div>
            </div>
            <div className="step">
              <p className="step__num">
                {en ? '02 · The model' : '02 · Het model'}
              </p>
              <h3>
                {en
                  ? 'Candidate predicates, ranked and scored.'
                  : 'Kandidaat-predicates, gerangschikt en gescoord.'}
              </h3>
              <p className="step__body">
                {en
                  ? "Each candidate scored characteristic by characteristic — so it's clear not just which, but why this one over that one."
                  : 'Elke kandidaat kenmerk voor kenmerk gescoord — zodat niet alleen duidelijk is welke, maar waarom deze boven die.'}
              </p>
              <div className="step__example">
                {en
                  ? 'K-numbers ranked · per-characteristic scores · closest on sensing modality, differs on energy source.'
                  : 'K-nummers gerangschikt · scores per kenmerk · dichtst op meetmodaliteit, verschilt op energiebron.'}
              </div>
            </div>
            <div className="step">
              <p className="step__num">
                {en ? '03 · The model' : '03 · Het model'}
              </p>
              <h3>
                {en ? 'A draft equivalence table.' : 'Een concept-gelijkwaardigheidstabel.'}
              </h3>
              <p className="step__body">
                {en
                  ? 'Subject against predicate, row by row, marked same or different — the skeleton of the submission, yours to edit.'
                  : 'Subject tegen predicate, rij voor rij, gemarkeerd gelijk of verschillend — het skelet van de indiening, van jou om te bewerken.'}
              </p>
              <div className="step__example">
                {en
                  ? 'Per row: SAME · DIFFERENT (different characteristic → does it raise new questions?).'
                  : 'Per rij: SAME · DIFFERENT (afwijkend kenmerk → roept dat nieuwe vragen op?).'}
              </div>
            </div>
            <div className="step">
              <p className="step__num">{en ? '04 · You' : '04 · Jij'}</p>
              <h3>{en ? 'The residual gap.' : 'Het resterende gat.'}</h3>
              <p className="step__body">
                {en
                  ? "The characteristics the predicate doesn't cover — the gap a reviewer will probe — noted before you file."
                  : 'De kenmerken die de predicate niet dekt — het gat waar een beoordelaar op zal doorvragen — genoteerd voordat je indient.'}
              </p>
              <div className="step__example">
                {en
                  ? 'Uncovered: home-use environment · cybersecurity for wireless link.'
                  : 'Niet gedekt: thuisgebruik-omgeving · cybersecurity voor de draadloze verbinding.'}
              </div>
            </div>
          </div>

          {/* Equivalence table mock */}
          <div className="eqtable">
            <div className="eqtable__frame">
              <div className="eqtable__head">
                <span>{en ? 'Characteristic' : 'Kenmerk'}</span>
                <span>{en ? 'Subject device' : 'Subject device'}</span>
                <span>Predicate K21xxxx</span>
                <span>{en ? 'Verdict' : 'Oordeel'}</span>
              </div>
              <div className="eqrow">
                <span className="eqrow__axis">
                  {en ? 'Intended use' : 'Beoogd gebruik'}
                </span>
                <span className="eqrow__val">
                  {en ? 'Arrhythmia detection, ambulatory' : 'Aritmiedetectie, ambulant'}
                </span>
                <span className="eqrow__val">
                  {en ? 'Arrhythmia detection, ambulatory' : 'Aritmiedetectie, ambulant'}
                </span>
                <span>
                  <span className="verdict verdict--same">SAME</span>
                </span>
              </div>
              <div className="eqrow">
                <span className="eqrow__axis">
                  {en ? 'Sensing modality' : 'Meetmodaliteit'}
                </span>
                <span className="eqrow__val">Single-lead ECG</span>
                <span className="eqrow__val">Single-lead ECG</span>
                <span>
                  <span className="verdict verdict--same">SAME</span>
                </span>
              </div>
              <div className="eqrow">
                <span className="eqrow__axis">
                  {en ? 'Energy source' : 'Energiebron'}
                </span>
                <span className="eqrow__val">
                  {en ? 'Rechargeable, wireless' : 'Oplaadbaar, draadloos'}
                </span>
                <span className="eqrow__val">
                  {en ? 'Disposable, wired' : 'Wegwerp, bedraad'}
                </span>
                <span>
                  <span className="verdict verdict--diff">DIFFERENT</span>
                </span>
              </div>
              <div className="eqrow">
                <span className="eqrow__axis">
                  {en ? 'Use environment' : 'Gebruiksomgeving'}
                </span>
                <span className="eqrow__val">
                  {en ? 'Home + clinical' : 'Thuis + klinisch'}
                </span>
                <span className="eqrow__val">
                  {en ? 'Clinical only' : 'Alleen klinisch'}
                </span>
                <span>
                  <span className="verdict verdict--gap">GAP</span>
                </span>
              </div>
            </div>
            <p className="eqtable__cap">
              {en ? 'Illustrative.' : 'Illustratief.'}
            </p>
          </div>
        </section>

        {/* Who builds it */}
        <section className="section">
          <div className="container">
            <div className="split">
              <div>
                <span className="label">
                  {en ? 'Who builds it' : 'Wie het bouwt'}
                </span>
                <h2 style={{ marginTop: 18 }}>
                  {en
                    ? 'We build models for decisions that have to hold up.'
                    : 'Wij bouwen modellen voor beslissingen die standhouden.'}
                </h2>
                <p className="body-text" style={{ marginTop: 24 }}>
                  {en ? (
                    <>
                      Thoughtful Oasis is an AI lab, and the architecture behind this model already
                      runs in two of the most accountability-heavy settings there are:{' '}
                      <strong>emergency command and government</strong>. Those are places where an
                      unexplained or unreliable answer isn't an option — which is exactly the
                      discipline a regulatory submission needs.
                    </>
                  ) : (
                    <>
                      Thoughtful Oasis is een AI-lab, en de architectuur achter dit model draait al
                      in twee van de meest verantwoordingszware omgevingen die er zijn:{' '}
                      <strong>crisiscommando en overheid</strong>. Dat zijn plekken waar een
                      onverklaard of onbetrouwbaar antwoord geen optie is — precies de discipline
                      die een indiening bij de toezichthouder nodig heeft.
                    </>
                  )}
                </p>
                <p className="body-text">
                  {en
                    ? 'This model is that architecture, trained for medical-device regulatory affairs — built from the ground up to produce a defensible predicate and a checkable equivalence table.'
                    : 'Dit model ís die architectuur, getraind voor regulatory affairs van medische hulpmiddelen — vanaf de grond opgebouwd om een verdedigbare predicate en een controleerbare gelijkwaardigheidstabel te leveren.'}
                </p>
              </div>
              <div>
                <div className="card modelspec">
                  <div className="modelspec__head">
                    <span className="modelspec__name">oasis_0 Asclepius 510k</span>
                    <span className="btn btn-secondary modelspec__badge">
                      {en ? 'Early access' : 'Vroege toegang'}
                    </span>
                  </div>
                  <div className="modelspec__item">
                    <span className="label">
                      {en ? 'Built for' : 'Gebouwd voor'}
                    </span>
                    <p className="body-text">
                      {en
                        ? 'Predicate search and per-characteristic substantial equivalence, with the residual gap made explicit.'
                        : 'Predicate-search en substantiële gelijkwaardigheid per kenmerk, met het resterende gat expliciet gemaakt.'}
                    </p>
                  </div>
                  <div className="modelspec__item">
                    <span className="label">
                      {en ? 'Trained on' : 'Getraind op'}
                    </span>
                    <p className="body-text">
                      {en
                        ? 'FDA controlled vocabularies and cleared-device records; in deployment, your own submission history. In-domain, not zero-shot.'
                        : 'Gecontroleerde FDA-woordenlijsten en records van toegelaten hulpmiddelen; in gebruik ook je eigen indieningshistorie. In-domein, niet zero-shot.'}
                    </p>
                  </div>
                  <div className="modelspec__item">
                    <span className="label">{en ? 'Runs' : 'Draait'}</span>
                    <p className="body-text">
                      {en
                        ? "On a proven, published architecture. EU-hosted or on-premise; your data isn't used to train shared models."
                        : 'Op een bewezen, gepubliceerde architectuur. In de EU gehost of on-premise; je data traint geen gedeelde modellen.'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How we deploy */}
        <section className="section section--tight">
          <div className="container">
            <div
              className="section-head"
              style={{ margin: '0 auto 40px', textAlign: 'center' }}
            >
              <span className="label">
                {en ? 'How we deploy' : 'Hoe we uitrollen'}
              </span>
              <h2 style={{ marginTop: 18 }}>
                {en
                  ? 'Decision-support, on your terms.'
                  : 'Beslissingsondersteuning, op jouw voorwaarden.'}
              </h2>
            </div>
            <div className="scope">
              <div className="scope__row">
                <span className="scope__mark scope__mark--do">DO</span>
                <p>
                  {en ? (
                    <>
                      <strong>
                        Shortlist and score predicates, then draft the equivalence table and
                        residual gap
                      </strong>{' '}
                      — the argument scaffold, editable end to end.
                    </>
                  ) : (
                    <>
                      <strong>
                        Predicates shortlisten en scoren, dan de gelijkwaardigheidstabel en het
                        resterende gat opstellen
                      </strong>{' '}
                      — het betoogskelet, van begin tot eind bewerkbaar.
                    </>
                  )}
                </p>
              </div>
              <div className="scope__row">
                <span className="scope__mark scope__mark--do">DO</span>
                <p>
                  {en ? (
                    <>
                      <strong>Train in-domain and run on your infrastructure.</strong> EU-hosted or
                      on-premise; your submissions never train shared models.
                    </>
                  ) : (
                    <>
                      <strong>In-domein trainen en op jouw infrastructuur draaien.</strong> In de EU
                      gehost of on-premise; je indieningen trainen nooit gedeelde modellen.
                    </>
                  )}
                </p>
              </div>
              <div className="scope__row">
                <span className="scope__mark scope__mark--dont">DON'T</span>
                <p>
                  {en ? (
                    <>
                      <strong>Make the substantial-equivalence determination.</strong> "Same
                      intended use" has soft edges reasonable experts read differently; the
                      specialist decides, the model scaffolds.
                    </>
                  ) : (
                    <>
                      <strong>De bepaling van substantiële gelijkwaardigheid maken.</strong> "Zelfde
                      beoogd gebruik" heeft zachte randen die redelijke experts anders lezen; de
                      specialist beslist, het model levert het skelet.
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
    </WebsiteLayout>
  );
};

export default Asclepius510k;
